if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this._autoplayInterval = null;
        this._autoplayPaused = false;

        if (this.elements.viewer && this.dataset.autoplay === 'true') {
          this._startAutoplayAfterLcp();
          this.elements.viewer.addEventListener('mouseenter', () => {
            this._autoplayPaused = true;
          });
          this.elements.viewer.addEventListener('mouseleave', () => {
            this._autoplayPaused = false;
          });
        }

        this._onTabVisibleBound = () => {
          if (document.hidden) return;
          this._onTabVisible();
        };
        document.addEventListener('visibilitychange', this._onTabVisibleBound);

        this._bindImageOnlyNavigation();

        const mediaList = this.elements.viewer?.querySelector('.product__media-list');
        if (mediaList) {
          const resetScroll = () => {
            mediaList.scrollLeft = 0;
          };
          resetScroll();
          requestAnimationFrame(resetScroll);
        }

        if (!this.mql.matches && this.elements.viewer) {
          this._bindMobileScrollSync();
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const btn = mediaToSwitch.querySelector('button');
          if (btn) {
            btn.addEventListener('click', () => {
              this.setActiveMedia(mediaToSwitch.dataset.target, false, true);
            });
          }
        });
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      _bindMobileScrollSync() {
        const list = this.elements.viewer?.querySelector('.product__media-list');
        if (!list) return;
        const sync = () => {
          const items = list.querySelectorAll('.product__media-item[data-media-id]');
          if (!items.length) return;
          const listRect = list.getBoundingClientRect();
          const center = listRect.left + listRect.width / 2;
          let best = null;
          let bestDist = Infinity;
          items.forEach((el) => {
            const r = el.getBoundingClientRect();
            const elCenter = r.left + r.width / 2;
            const dist = Math.abs(elCenter - center);
            if (dist < bestDist) {
              bestDist = dist;
              best = el;
            }
          });
          if (best && !best.classList.contains('is-active')) {
            list.querySelectorAll('.product__media-item').forEach((el) => el.classList.remove('is-active'));
            best.classList.add('is-active');
            this.playActiveMedia(best);
            const thumb = this.elements.thumbnails?.querySelector(`[data-target="${best.dataset.mediaId}"]`);
            if (thumb) this.setActiveThumbnail(thumb);
          }
        };
        list.addEventListener('scroll', debounce(sync, 80));
      }

      _getImageOnlySlides() {
        const all = this.elements.viewer?.querySelectorAll('.product__media-item[data-media-id]');
        return all ? Array.from(all).filter((el) => !el.classList.contains('product__media-item--full')) : [];
      }

      _bindImageOnlyNavigation() {
        const nextBtn = this.elements.viewer?.querySelector('.slider-button--next');
        const prevBtn = this.elements.viewer?.querySelector('.slider-button--prev');
        const list = this._getImageOnlySlides();
        if (list.length < 2) return;

        const go = (delta) => {
          const current = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]');
          let index = list.indexOf(current);
          if (index === -1) index = 0;
          index = (index + delta + list.length) % list.length;
          this.setActiveMedia(list[index].dataset.mediaId, false, true);
        };

        if (nextBtn) {
          nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            go(1);
          });
        }
        if (prevBtn) {
          prevBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            go(-1);
          });
        }
      }

      _startAutoplayAfterLcp() {
        const start = () => {
          if (this._autoplayInterval) return;
          this._startAutoplay();
        };
        const afterLcp = () => {
          if (window.requestIdleCallback) {
            requestIdleCallback(start, { timeout: 2500 });
          } else {
            setTimeout(start, 400);
          }
        };
        if (typeof PerformanceObserver === 'undefined') {
          setTimeout(afterLcp, 4000);
          return;
        }
        try {
          let finished = false;
          const po = new PerformanceObserver((list) => {
            if (!list.getEntries().length) return;
            finished = true;
            try {
              po.disconnect();
            } catch (e) {
              /* ignore */
            }
            afterLcp();
          });
          po.observe({ type: 'largest-contentful-paint', buffered: true });
          setTimeout(() => {
            if (!finished) afterLcp();
          }, 5000);
        } catch (e) {
          setTimeout(afterLcp, 4000);
        }
      }

      _startAutoplay() {
        const list = this._getImageOnlySlides();
        if (list.length < 2) return;
        const speed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
        this._autoplayInterval = setInterval(() => {
          if (this._autoplayPaused) return;
          const current = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]');
          if (!current) return;
          if (current.classList.contains('product__media-item--full')) return;
          let index = list.indexOf(current);
          if (index === -1) {
            index = 0;
          } else {
            index = (index + 1) % list.length;
          }
          const nextId = list[index].dataset.mediaId;
          this.setActiveMedia(nextId, false);
        }, speed);
      }

      _onTabVisible() {
        const list = this.elements.viewer?.querySelectorAll('.product__media-item[data-media-id]');
        if (!list?.length) return;
        const active = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]');
        if (!active) {
          const first = list[0];
          first?.classList.add('is-active');
          this.playActiveMedia(first);
          this.setActiveThumbnail(
            this.elements.thumbnails?.querySelector(`[data-target="${first?.dataset?.mediaId}"]`),
          );
          return;
        }
        active.classList.remove('media-gallery-fade-out', 'media-gallery-fade-in');
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => {
          if (el !== active) el.classList.remove('is-active');
        });
        active.classList.add('is-active');
        this.playActiveMedia(active);
        const thumb = this.elements.thumbnails?.querySelector(`[data-target="${active.dataset.mediaId}"]`);
        if (thumb) this.setActiveThumbnail(thumb);
        if (this._autoplayInterval && this.dataset.autoplay === 'true') {
          clearInterval(this._autoplayInterval);
          this._autoplayInterval = null;
          this._startAutoplay();
        }
      }

      disconnectedCallback() {
        if (this._onTabVisibleBound) {
          document.removeEventListener('visibilitychange', this._onTabVisibleBound);
        }
        if (this._autoplayInterval) {
          clearInterval(this._autoplayInterval);
          this._autoplayInterval = null;
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, userInitiated = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        if (userInitiated && this._autoplayInterval && this.dataset.autoplay === 'true') {
          clearInterval(this._autoplayInterval);
          this._autoplayInterval = null;
          this._startAutoplay();
        }
        const currentActive = this.elements.viewer.querySelector('.product__media-item.is-active[data-media-id]');
        const isDesktopThumbnail =
          this.mql.matches && this.elements.viewer.closest('.product--thumbnail, .product--thumbnail_slider');
        const useFade = isDesktopThumbnail && !prepend && currentActive && currentActive !== activeMedia;

        if (useFade) {
          const mediaWrapper = this.elements.viewer.closest('.product__media-wrapper');
          const stickyHost = mediaWrapper?.querySelector(':scope > .product__media-sticky') || mediaWrapper;
          const badges =
            mediaWrapper?.querySelector('.product__media-badges') ||
            mediaWrapper?.querySelector('.product-media-container .product__media-badges');
          if (stickyHost && badges && badges.parentElement !== stickyHost) {
            stickyHost.prepend(badges);
          }
          document.body.dataset.mediaGalleryTransitioning = '1';
          currentActive.classList.add('media-gallery-fade-out');
          const onOut = (e) => {
            if (e.target !== currentActive || e.propertyName !== 'opacity') return;
            currentActive.removeEventListener('transitionend', onOut);
            currentActive.classList.remove('is-active', 'media-gallery-fade-out');
            activeMedia.classList.add('is-active', 'media-gallery-fade-in');
            this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => {
              if (el !== activeMedia) el.classList.remove('is-active');
            });
            requestAnimationFrame(() => {
              activeMedia.offsetHeight;
              requestAnimationFrame(() => {
                activeMedia.classList.remove('media-gallery-fade-in');
              });
            });
            this._afterSetActiveMedia(activeMedia, mediaId, userInitiated);
            setTimeout(() => {
              document.body.removeAttribute('data-media-gallery-transitioning');
              document.dispatchEvent(new CustomEvent('media-gallery-move-badges'));
            }, 480);
          };
          currentActive.addEventListener('transitionend', onOut);
          return;
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active', 'media-gallery-fade-out', 'media-gallery-fade-in');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this._afterSetActiveMedia(activeMedia, mediaId, userInitiated);
      }

      _afterSetActiveMedia(activeMedia, mediaId, userInitiated = false) {
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (userInitiated && window.innerWidth > 749) {
            const activeMediaRect = activeMedia.getBoundingClientRect();
            if (activeMediaRect.top < -0.5) {
              const top = activeMediaRect.top + window.scrollY;
              window.scrollTo({ top: top, behavior: 'smooth' });
            }
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.elements.thumbnails?.slider?.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
        this._playActiveItemMedia(activeItem);
      }

      _playActiveItemMedia(activeItem) {
        const youtube = activeItem.querySelector('.js-youtube');
        if (youtube?.contentWindow) {
          try {
            youtube.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
          } catch (_) {}
          return;
        }
        const vimeo = activeItem.querySelector('.js-vimeo');
        if (vimeo?.contentWindow) {
          try {
            vimeo.contentWindow.postMessage('{"method":"play"}', '*');
          } catch (_) {}
          return;
        }
        const video = activeItem.querySelector('video');
        if (video && video.getAttribute('autoplay')) {
          video.play().catch(() => {});
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    },
  );
}

(function () {
  let badgesElement = null;

  function moveBadgesToActiveImage() {
    const mediaWrapper = document.querySelector('.product__media-wrapper');
    if (!mediaWrapper) return;

    const stickyHost = mediaWrapper.querySelector(':scope > .product__media-sticky') || mediaWrapper;

    if (!badgesElement) {
      badgesElement = mediaWrapper.querySelector('.product__media-badges');
      if (!badgesElement) {
        badgesElement = mediaWrapper.querySelector('.product-media-container .product__media-badges');
      }
    }
    if (!badgesElement) return;

    // Štítky držet na obalu galerie (.product__media-sticky nebo wrapper), ne uvnitř slidů — bez CLS.
    // Se sticky sloupcem musí být uvnitř stejného prvku jako media-gallery, aby scrollovaly s fotkou.
    if (badgesElement.parentElement !== stickyHost) {
      stickyHost.prepend(badgesElement);
    }
  }

  function init() {
    moveBadgesToActiveImage();

    const mediaGallery = document.querySelector('media-gallery');
    if (mediaGallery) {
      const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
      if (viewer) {
        viewer.addEventListener('slideChanged', () => {
          setTimeout(moveBadgesToActiveImage, 50);
        });
      }
    }

    document.addEventListener('media-gallery-move-badges', moveBadgesToActiveImage);

    const mediaList = document.querySelector('.product__media-list');
    if (mediaList) {
      const observer = new MutationObserver(() => {
        moveBadgesToActiveImage();
      });
      observer.observe(mediaList, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true,
      });
    }

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(moveBadgesToActiveImage, 100);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  setTimeout(init, 100);
  setTimeout(init, 500);
})();
